import React, { useMemo } from 'react';
import {
    Box,
    Tab,
    Tabs,
    ExtendButtonBase as IMuiExtendButtonBase,
    TabTypeMap as IMuiTabTypeMap,
    CSSObject,
} from '@mui/material';
import { IComponentCss } from '@tap/mui-ui';

/*
*   Вертикальные табы
*/

type IVerticalTabsCustom = {
    tabsArray: ITabsArrayItem[],
    defaultTab?: number,
    height?: number | string,
    setCurrentTabIndex?: ((index: number) => void) | undefined,
    saveCurrentActiveTabAs?: string | undefined,
    isCacheTabs?: boolean,
    tabsWrapStyle?: CSSObject,
    tabsStyle?: CSSObject,
}

type ITabsArrayItem = {
    tabButton: {
        label: string | React.ReactNode,
        otherProps?: Omit<IMuiExtendButtonBase<IMuiTabTypeMap>, 'label'>,
    },
    tabComponent: React.ReactNode,
    isHide?: boolean,
}

type TabPanelProps = {
    children?: React.ReactNode,
    tabIndex: number,
    value: number,
    isCacheTabs: boolean,
}

const css: IComponentCss = {
    tabsWrap: {
        border: '1px solid #D7D7D7',
        borderRadius: '4px',
        flexGrow: 1,
        display: 'flex',
    },
    tabs: {
        borderRight: 1,
        borderColor: 'divider',
        boxSizing: 'content-box',
        overflow: 'hidden',
        minWidth: '270px',
        '& .MuiTab-root.Mui-selected': {
            background: 'rgba(194, 214, 241, 0.2)',
        },
    },
}


function TabPanel({
    children,
    value,
    tabIndex,
    isCacheTabs,
    ...other
}: TabPanelProps) {
    return (
        <div
            role="tabpanel"
            hidden={value !== tabIndex && !isCacheTabs}
            id={`vertical-tabpanel-${tabIndex}`}
            aria-labelledby={`vertical-tab-${tabIndex}`}
            {...other}
            style={{
                left: 0,
                width: '100%',
                ...(isCacheTabs &&
                {
                    visibility: value === tabIndex ? 'visible' : 'hidden',
                    position: value === tabIndex ? 'relative' : 'absolute',
                }),
            }}
        >
            {(!isCacheTabs && value === tabIndex) && <>{children}</>}

            {isCacheTabs && <>{children}</>}
        </div >
    );
}

function a11yProps(index: number) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}


export function VerticalTabsCustom({
    tabsArray,
    defaultTab = 0,
    height = undefined,
    setCurrentTabIndex = undefined,
    saveCurrentActiveTabAs = undefined,
    isCacheTabs = false,
    tabsWrapStyle = undefined,
    tabsStyle = undefined,
}: IVerticalTabsCustom) {

    const [currentTab, setCurrentTab] = React.useState(
        saveCurrentActiveTabAs
            ? (Number(localStorage.getItem(saveCurrentActiveTabAs)) || defaultTab)
            : defaultTab,
    );

    const handleChangeCurrentTabOnClick = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
        if (setCurrentTabIndex)
            setCurrentTabIndex(newValue);
        if (saveCurrentActiveTabAs)
            localStorage.setItem(saveCurrentActiveTabAs, newValue.toString());
    }

    const tabList = useMemo(() => tabsArray.map((item, tabIndex) => ({ tabIndex, ...item })), [tabsArray]);

    return (
        <Box
            sx={{
                ...css.tabsWrap,
                height: height ? height : 'auto',
                ...tabsWrapStyle,
            }}
        >

            <Tabs
                orientation="vertical"
                variant="scrollable"
                value={currentTab}
                onChange={handleChangeCurrentTabOnClick}
                aria-label="Vertical tabs"
                sx={{
                    ...css.tabs,
                    ...tabsStyle,
                }}
            >
                {tabList.map((tab) => (
                    !tab.isHide &&
                    <Tab
                        key={tab.tabIndex}
                        label={tab.tabButton.label}
                        {...a11yProps(tab.tabIndex)}
                        {...tab.tabButton.otherProps}
                    />
                ))
                }
            </Tabs>

            {tabList.map((tab) => (
                !tab.isHide &&
                <TabPanel
                    key={tab.tabIndex}
                    value={currentTab}
                    tabIndex={tab.tabIndex}
                    isCacheTabs={isCacheTabs}
                >
                    {tab.tabComponent}
                </TabPanel>
            ))
            }

        </Box>
    );
}
